import { useCallback, useEffect, useRef, useState } from "react";
import { adminStatsApi } from "@/lib/api/adminStats";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/roles";

const ADMIN_ROLES = new Set([
    ROLES.SUPER_ADMIN, ROLES.OPS_ADMIN, ROLES.FINANCE_ADMIN, ROLES.SALES_AGENT,
]);

const POLL_MS = 60_000;

/**
 * Polls /admin/stats/nav-counts every 60s + on window focus, returns:
 *   { "kyc.pending": 3, "offers.pending": 1, "members.pending_review": 0, "disputes.open": 2 }
 *
 * Returns an empty object for non-admin roles (no fetch happens).
 * The keys match the `badge.source` values declared in navConfig.js.
 */
export function useNavCounts() {
    const { user } = useAuth();
    const [counts, setCounts] = useState({});
    const timer = useRef(null);
    const isAdmin = !!user && ADMIN_ROLES.has(user.role);

    const refetch = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const data = await adminStatsApi.navCounts();
            setCounts({
                "kyc.pending":            data.kyc_pending || 0,
                "offers.pending":         data.offers_pending || 0,
                "members.pending_review": data.members_pending_review || 0,
                "disputes.open":          data.disputes_open || 0,
            });
        } catch (_err) {
            // Silent — sidebar shouldn't error on a flaky badge call.
        }
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin) { setCounts({}); return undefined; }
        refetch();
        timer.current = setInterval(refetch, POLL_MS);
        const onFocus = () => refetch();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(timer.current);
            window.removeEventListener("focus", onFocus);
        };
    }, [isAdmin, refetch]);

    return counts;
}
